import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { UpdateVisitasEstadoDto, VisitasDto } from '../../../types/visitas';

interface VisitaEstadoDialogProps {
  open: boolean;
  action: 'iniciar' | 'completar' | 'omitir';
  visita: VisitasDto | null;
  saving?: boolean;
  onClose: () => void;
  onSubmit: (payload: UpdateVisitasEstadoDto) => Promise<void>;
}

const titleByAction: Record<'iniciar' | 'completar' | 'omitir', string> = {
  iniciar: 'Iniciar visita',
  completar: 'Completar visita',
  omitir: 'Omitir visita',
};

const VisitaEstadoDialog: React.FC<VisitaEstadoDialogProps> = ({
  open,
  action,
  visita,
  saving = false,
  onClose,
  onSubmit,
}) => {
  const [motivoOmitida, setMotivoOmitida] = useState('');
  const [observaciones, setObservaciones] = useState('');

  useEffect(() => {
    if (!open) return;
    setMotivoOmitida('');
    setObservaciones(visita?.observaciones ?? '');
  }, [open, visita]);

  const isOmitir = action === 'omitir';
  const canSubmit = useMemo(() => !saving, [saving]);

  const handleSubmit = async () => {
    await onSubmit({
      motivoOmitida: isOmitir ? motivoOmitida || null : null,
      observaciones: observaciones || null,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{titleByAction[action]}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Visita #{visita?.idVisita} - Cliente {visita?.nroCliente}
        </Typography>
        {isOmitir && (
          <TextField
            fullWidth
            label="Motivo de omisión"
            value={motivoOmitida}
            onChange={(e) => setMotivoOmitida(e.target.value)}
            sx={{ mb: 2 }}
          />
        )}
        <TextField
          fullWidth
          multiline
          minRows={3}
          label="Observaciones"
          value={observaciones}
          onChange={(e) => setObservaciones(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit}>
          Confirmar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VisitaEstadoDialog;
